/**
 * T.10 S4 (review 7, 9, 11). Rules the subgraph and the wizard must agree on,
 * in one place: what a job key names, and how a typed task is cleaned and
 * capped. Pure.
 */

/** The most tasks one job keeps (16 O*NET chips plus a few typed ones). */
export const MAX_CLAIMS = 20;
/** user_task_claim.typed_text is 1 to 200 characters (migration M2). */
export const MAX_TYPED = 200;

export type JobKeyKind = 'military' | 'civilian' | 'transcript';

/**
 * military `branchcode|moccode`, civilian `onetcode#n` (spec 6.2), and a
 * military job found only on the transcript, `jst:<moc>`, where the code is
 * the transcript's, trimmed and upper-cased by resumeStarter (letters,
 * digits, and the odd hyphen, dot, slash or inner space, up to 16). Anything
 * else (a job only on a resume, one typed on the empty board) names no job
 * the server knows.
 */
export function jobKeyKind(key: string): JobKeyKind | null {
  if (/^jst:[A-Z0-9](?:[A-Z0-9 .\-/]{0,14}[A-Z0-9.])?$/.test(key)) {
    return 'transcript';
  }
  if (/^\d{2}-\d{4}\.\d{2}#\d{1,3}$/.test(key)) {
    return 'civilian';
  }
  // The key holds the codes as stored (militaryJobKey), so only its shape is checked.
  if (/^[A-Za-z]{1,3}\|[^|#\s][^|#]{0,15}$/.test(key)) {
    return 'military';
  }
  return null;
}

// Control characters, and the invisible ones (zero-width, bidirectional marks, BOM).
const INVISIBLE = new RegExp('[\u200B-\u200F\u202A-\u202E\u2060-\u206F\uFEFF]', 'g');
// Matching control characters is the point here.
// eslint-disable-next-line no-control-regex
const CONTROL = new RegExp('[\u0000-\u001F\u007F-\u009F\u2028\u2029]', 'g');

/** A typed task as it is shown and saved: invisible characters gone, spaces collapsed. */
export function cleanTypedTask(text: string): string {
  return String(text ?? '')
    .replace(INVISIBLE, '')
    .replace(CONTROL, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
